import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../config/api';

const API = API_BASE_URL;

const fmtTime = (ts) => {
  const d = new Date(ts);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' +
    d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const fmtListTime = (ts) => {
  const d = new Date(ts);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const diff = (now - d) / 86400000;
  if (diff < 7) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

/**
 * ConversationsTab — an inbox of SMS threads. Left: candidates with texts,
 * newest first. Right: the selected thread as chat bubbles, with an inline
 * reply box. Replies send from the recruiter's own number via the backend.
 */
const ConversationsTab = ({ candidateId: fixedCandidateId = null, candidateName: fixedName = null, candidateNumber: fixedNumber = null, onClose = null } = {}) => {
  const single = fixedCandidateId != null; // single-candidate (sidepanel) mode
  const [convos, setConvos] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [scope, setScope] = useState('firm'); // firm | mine
  const [activeId, setActiveId] = useState(fixedCandidateId || null);
  const [thread, setThread] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const convosRef = useRef([]);
  useEffect(() => { convosRef.current = convos; }, [convos]);
  // In single mode, keep the active id pinned to the given candidate.
  useEffect(() => { if (single && fixedCandidateId != null) setActiveId(fixedCandidateId); }, [single, fixedCandidateId]);

  const token = localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadList = useCallback(async (silent = false) => {
    try {
      const qs = scope === 'mine' ? '?scope=mine' : '';
      const res = await fetch(`${API}/api/outreach/conversations${qs}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setConvos(prev => {
          // Cheap change check: compare a signature of ids + last timestamps.
          const sig = (arr) => arr.map(c => `${c.candidate_id}:${c.last_at}`).join('|');
          return sig(prev) === sig(data) ? prev : data;
        });
        if (!activeId && data.length > 0) setActiveId(data[0].candidate_id);
      }
    } catch (e) { console.error(e); }
    finally { if (!silent) setLoadingList(false); }
    // eslint-disable-next-line
  }, [scope]);

  const loadThread = useCallback(async (candidateId, silent = false) => {
    if (!candidateId) return;
    if (!silent) setLoadingThread(true);
    try {
      const res = await fetch(`${API}/api/outreach/conversations/${candidateId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        // Only update if the message count or latest id changed, to avoid
        // needless re-renders / scroll jumps on every poll.
        setThread(prev => {
          const prevKey = prev.length ? `${prev.length}:${prev[prev.length - 1]?.id}` : '0';
          const nextKey = data.length ? `${data.length}:${data[data.length - 1]?.id}` : '0';
          return prevKey === nextKey ? prev : data;
        });
      }
    } catch (e) { console.error(e); }
    finally { if (!silent) setLoadingThread(false); }
    // eslint-disable-next-line
  }, []);

  useEffect(() => { if (!single) loadList(); else setLoadingList(false); }, [loadList, single]);
  useEffect(() => { if (activeId) loadThread(activeId); }, [activeId, loadThread]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread]);

  // Smart refresh: silently poll the lightweight list; only refetch the open
  // thread when that conversation's latest timestamp actually advances. Pauses
  // while the tab is hidden. No blind full-reload / spinner flicker.
  const lastActiveAtRef = useRef(null);
  useEffect(() => {
    const tick = async () => {
      if (document.hidden) return;
      if (!single) await loadList(true);
      if (activeId) {
        if (single) { loadThread(activeId, true); return; }
        const c = convosRef.current.find(x => x.candidate_id === activeId);
        const at = c?.last_at || null;
        if (at !== lastActiveAtRef.current) {
          lastActiveAtRef.current = at;
          loadThread(activeId, true);
        }
      }
    };
    const iv = setInterval(tick, 20000);
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
    // eslint-disable-next-line
  }, [activeId]);

  const active = single
    ? { candidate_id: fixedCandidateId, candidate_name: fixedName, from_number: fixedNumber }
    : convos.find(c => c.candidate_id === activeId);

  const sendReply = async () => {
    if (!reply.trim() || !activeId || sending) return;
    setSending(true);
    const text = reply.trim();
    setReply('');
    try {
      const res = await fetch(`${API}/api/outreach/conversations/${activeId}/reply`, {
        method: 'POST', headers, body: JSON.stringify({ body: text }),
      });
      if (res.ok) {
        const msg = await res.json();
        setThread(prev => [...prev, msg]);
        loadList();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to send');
        setReply(text);
      }
    } catch (e) { console.error(e); setReply(text); }
    finally { setSending(false); }
  };

  return (
    <div className="flex h-full min-h-0 border border-gray-200 rounded-lg overflow-hidden bg-[color:var(--list-block-bg)]">
      {/* Left: conversation list — only in the full (firm-wide) view */}
      {!single && (
      <div className="w-72 flex-shrink-0 border-r border-gray-200 flex flex-col min-h-0">
        <div className="flex-shrink-0 p-2 border-b border-gray-200">
          <div className="inline-flex h-[28px] border border-gray-300 rounded overflow-hidden bg-white w-full">
            <button onClick={() => setScope('firm')}
              className={`flex-1 text-xs transition-colors ${scope === 'firm' ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}>Whole firm</button>
            <button onClick={() => setScope('mine')}
              className={`flex-1 text-xs border-l border-gray-300 transition-colors ${scope === 'mine' ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}>Just mine</button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto min-h-0">
          {loadingList ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600" /></div>
          ) : convos.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-400">No SMS conversations yet.</div>
          ) : convos.map(c => (
            <button key={c.candidate_id} onClick={() => setActiveId(c.candidate_id)}
              className={`w-full text-left px-3 py-2.5 border-b border-gray-100 transition-colors ${activeId === c.candidate_id ? 'bg-emerald-50' : 'hover:bg-gray-50'}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-gray-900 truncate">{c.candidate_name || 'Unknown'}</span>
                <span className="text-[10px] text-gray-400 flex-shrink-0">{fmtListTime(c.last_at)}</span>
              </div>
              <div className="text-xs text-gray-500 truncate mt-0.5">
                {c.last_direction === 'outbound' && <span className="text-gray-400">You: </span>}
                {c.last_body}
              </div>
            </button>
          ))}
        </div>
      </div>
      )}

      {/* Right: thread */}
      <div className="flex-1 flex flex-col min-h-0 min-w-0">
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-sm text-gray-400">Select a conversation</div>
        ) : (
          <>
            <div className="flex-shrink-0 px-4 py-2.5 border-b border-gray-200 bg-[color:var(--list-header-bg)]">
              <div className="text-sm font-semibold" style={{ color: 'var(--list-header-text)' }}>{active.candidate_name || 'Unknown'}</div>
              <div className="text-[11px] text-gray-400">{active.from_number}</div>
            </div>

            <div className="flex-1 overflow-y-auto min-h-0 p-4 space-y-2">
              {loadingThread ? (
                <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600" /></div>
              ) : thread.map(m => (
                <div key={m.id} className={`flex min-w-0 ${m.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] min-w-0 rounded-lg px-3 py-2 ${m.direction === 'outbound' ? 'bg-emerald-600 text-white' : 'bg-white border border-gray-200 text-gray-800'}`}>
                    <div className="text-sm whitespace-pre-wrap" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{m.body}</div>
                    <div className={`text-[10px] mt-1 ${m.direction === 'outbound' ? 'text-emerald-100' : 'text-gray-400'}`}>{fmtTime(m.created_at)}</div>
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>

            <div className="flex-shrink-0 p-3 border-t border-gray-200 flex items-end gap-2">
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                placeholder="Type a reply… (Enter to send)"
                rows={1}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-1 focus:ring-emerald-400 max-h-32"
              />
              <button onClick={sendReply} disabled={!reply.trim() || sending}
                className="h-[38px] px-4 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 disabled:opacity-50 flex-shrink-0">
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ConversationsTab;
